import React from 'react';
import { DollarSignInCircle } from '@/icons/dollar-sign-in-circle';
import { formatCurrency } from '@/components/tables/table-utils';
import { cn } from '@/lib/utils';
import { isNil } from 'lodash';

type Props = {
  allocated: number;
  total: number;
  hasMismatch?: boolean;
};
export function PriceProgressBar({ allocated, total, hasMismatch = false }: Props) {
  const pct = calcBarProportion(allocated, total);
  const flagMismatch = hasMismatch && pct !== 100;

  return (
    <div className="flex basis-2/3 flex-col gap-1">
      <div className="flex items-center gap-2">
        <DollarSignInCircle />
        <div className="progress-bar-shadow w-full rounded-xl bg-gray-100">
          {pct > 100 ? <OverallocatedBar percentage={pct} /> : <AllocatedBar percentage={pct} />}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="text-xs">
          <span
            className={cn(
              'rounded px-1 py-0.5 text-sm font-semibold',
              flagMismatch ? 'text-red-800' : calcAllocatedColor(pct),
              flagMismatch ? 'bg-red-100' : calcAllocatedHighlight(pct)
            )}>
            {formatCurrency(allocated)}
          </span>{' '}
          of <span className="font-semibold">{formatCurrency(total)}</span> Campaign Price Allocated
        </div>
        {pct === 100 && (
          <div className="rounded bg-lime-100 px-1 text-sm font-semibold text-lime-700">
            Fully Allocated
          </div>
        )}
        {pct < 100 && (
          <div className="text-xs">
            <span className="text-sm font-semibold text-yellow-700">
              {formatCurrency(total - allocated)}
            </span>{' '}
            Remaining
          </div>
        )}
        {pct > 100 && (
          <div className="text-xs">
            <span className="rounded bg-red-100 px-1 py-0.5 text-sm font-semibold text-red-800">
              {formatCurrency(allocated - total)}
            </span>{' '}
            Over-allocated
          </div>
        )}
      </div>
    </div>
  );
}

function OverallocatedBar({ percentage }: { percentage: number }) {
  const overage = ((percentage - 100) / percentage) * 100;
  return (
    <div className="flex h-4 flex-1">
      <div
        className={cn(
          'progress-bar-inner-shadow bg-linear-to-r rounded-l-xl from-[#A3E635] to-[#65A30D]'
        )}
        style={{ width: `${100 - overage}%` }}
      />
      <div
        className={cn(
          'bg-linear-to-r rounded-r-xl from-red-500 to-[#BE123C] shadow-[0_0_4px_0_#BE123C]'
        )}
        style={{ width: `${overage}%` }}
      />
    </div>
  );
}

function AllocatedBar({ percentage }: { percentage: number }) {
  return (
    <div className="flex h-4 flex-1">
      <div
        className={cn(
          'progress-bar-inner-shadow rounded-xl',
          percentage === 100
            ? 'bg-linear-to-r from-[#A3E635] to-[#65A30D]'
            : 'bg-linear-to-r from-[#FBBF24] to-[#CA8A04]'
        )}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
}

function calcBarProportion(allocated: number | null | undefined, total: number) {
  if (isNil(allocated) || total === 0) return 0;
  return (allocated / total) * 100;
}

function calcAllocatedColor(pct: number) {
  if (pct === 100) return 'text-lime-700';
  if (pct > 100) return 'text-red-800';
  return 'text-yellow-700';
}

function calcAllocatedHighlight(pct: number) {
  if (pct > 100) return 'bg-red-100';
  return null;
}
